/**
 * Animation Utilities
 * Scroll reveal and intersection observer logic
 */

/**
 * Initialize scroll reveal animations
 * Adds 'in' class to elements with 'reveal' class when they enter viewport
 */
export function initScrollReveal(): IntersectionObserver | undefined {
  const revealElements = document.querySelectorAll('.reveal');

  if (!revealElements.length) return;

  const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in');
        // Optional: unobserve after animation to improve performance
        // observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  revealElements.forEach((el) => observer.observe(el));

  return observer;
}

/**
 * Initialize navigation scroll behavior
 * Adds 'scrolled' class to nav when user scrolls past threshold
 */
export function initNavScroll(): (() => void) | undefined {
  const nav = document.getElementById('nav');
  if (!nav) return;

  const handleScroll = (): void => {
    if (window.scrollY > 50) {
      nav.classList.add('scrolled');
    } else {
      nav.classList.remove('scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll);

  return () => window.removeEventListener('scroll', handleScroll);
}

/**
 * Smooth scroll to anchor links
 */
export function initSmoothScroll(): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function (e: MouseEvent) {
      const href = this.getAttribute('href');

      // Skip empty hash or just '#'
      if (href === '#' || href === '') return;

      e.preventDefault();

      const target = document.querySelector<HTMLElement>(href!);
      if (target) {
        const navHeight = 76; // Fixed nav height
        const targetPosition = target.offsetTop - navHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    });
  });
}

/**
 * Initialize mobile menu toggle
 */
export function initMobileMenu(): (() => void) | undefined {
  const hamburger = document.getElementById('hamburger');
  const mobileMenu = document.getElementById('mobile-menu');

  if (!hamburger || !mobileMenu) return;

  const toggleMenu = (): void => {
    const isExpanded = hamburger.getAttribute('aria-expanded') === 'true';
    hamburger.setAttribute('aria-expanded', String(!isExpanded));
    (mobileMenu as HTMLElement).style.display = isExpanded ? 'none' : 'block';
  };

  hamburger.addEventListener('click', toggleMenu);

  // Close menu when clicking a link
  const mobileLinks = mobileMenu.querySelectorAll('a');
  mobileLinks.forEach((link) => {
    link.addEventListener('click', () => {
      hamburger.setAttribute('aria-expanded', 'false');
      (mobileMenu as HTMLElement).style.display = 'none';
    });
  });

  return () => hamburger.removeEventListener('click', toggleMenu);
}

/**
 * Initialize all animations
 */
export function initAnimations(): void {
  initScrollReveal();
  initNavScroll();
  initSmoothScroll();
  initMobileMenu();
}

export default {
  initScrollReveal,
  initNavScroll,
  initSmoothScroll,
  initMobileMenu,
  initAnimations,
};
